import { useMemo, useState } from 'react';

import { PagePagination } from '@/components/pagination';
import { ProtectedComponent } from '@/components/ProtectedComponent';
import { DataTable } from '@/components/ui/data-table';

import { useCallsQuery } from '@/query/call.query';

import { DEFAULT_PAGE_SIZE, Permissions } from '@/constants';

import { CallDetailPage } from '../CallDetailPage/CallDetailPage';

import { CallsHeader } from './components/calls-header';
import { getColumns } from './components/columns';
import { CallEmptyResult } from './components/empty';

import { useCallsListStore } from '@/store/calls-list.store';
import type { Call } from '@/types/call.type';
import { type ColumnDef } from '@tanstack/react-table';

export default function Calls() {
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);

  const { currentPage, filters, pageSize, setCurrentPage, setPageSize, sort, setSort, query } =
    useCallsListStore();

  const { data, isLoading } = useCallsQuery({
    page: currentPage,
    page_size: pageSize,
    query,
    filters,
  });

  const calls: Call[] = data?.data || [];
  const meta = data?.meta;

  const columns = useMemo<ColumnDef<Call>[]>(() => getColumns(), []);

  return (
    <>
      <div className="flex flex-col h-full space-y-6">
        {/** Search and Filter */}
        <CallsHeader calls={calls} />

        <div className="bg-background border rounded-lg">
          <DataTable
            columns={columns}
            data={calls}
            sorting={sort}
            isLoading={isLoading}
            onSortingChange={setSort}
            onRowClick={row => setSelectedCall(row)}
            emptyState={<CallEmptyResult />}
          />

          {/* PagePagination */}
          <PagePagination
            isLoading={isLoading}
            currentPage={meta?.page || 1}
            pageSize={meta?.page_size || DEFAULT_PAGE_SIZE}
            totalItems={meta?.total_items || 0}
            totalPages={meta?.total_pages || 1}
            setPageSize={size => {
              setPageSize(size);
              setCurrentPage(1); // Reset to first page when page size changes
            }}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>

      {/* Call Detail Sheet */}
      <ProtectedComponent requiredPermissions={[Permissions.CallsReadDetail]}>
        {selectedCall && (
          <CallDetailPage toggle={() => setSelectedCall(null)} callId={selectedCall.id} />
        )}
      </ProtectedComponent>
    </>
  );
}
